/**
 * @file CustomersListPage.tsx
 * @description Реестр клиентов магазина (phase08.5; phase19.31 — таблица по
 * образцу «Список», разбор A).
 *
 * Страница — раскладка: заголовок в верхней панели (`CabinetPageHeader`) с
 * главным действием «+ Клиент», строка фильтров (применяются сразу), таблица
 * от 1024 / компактный список на телефоне (`ResponsiveList`), постраничный
 * вывод. Фильтры и страница — в адресе (`useCustomersRegistry`), колонки —
 * `CUSTOMER_REGISTRY_COLUMNS`.
 */

import { Plus, RefreshCw, Search, Users } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";

import { useHasPermission } from "../../../auth/hooks";
import { CrmPagination } from "../../../components/crm/CrmPagination";
import { CustomerEditDialog } from "../../../components/customers/CustomerEditDialog";
import { CUSTOMER_REGISTRY_COLUMNS } from "../../../components/customers/registry/customer-columns";
import { CustomersFilterBar } from "../../../components/customers/registry/CustomersFilterBar";
import { useCustomersRegistry } from "../../../components/customers/registry/useCustomersRegistry";
import { Button } from "../../../components/ui/button";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import type { PageAction } from "../../../components/ui/page-actions";
import { ResponsiveList } from "../../../components/ui/ResponsiveList";
import { customerTitle } from "../../../lib/crm-customer-view";
import { customersQueryFiltered } from "../../../lib/crm-customers-query";
import { pluralRu } from "../../../lib/plural";

export function CustomersListPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const base = `/shop/${slug}/customers`;
  const canManage = useHasPermission("SHOP_MANAGE_CUSTOMERS");
  const registry = useCustomersRegistry();
  const { query, setQuery } = registry;
  const [creating, setCreating] = useState(false);
  const filtered = customersQueryFiltered(query);

  const menu: PageAction[] = [
    { key: "refresh", label: "Обновить список", icon: RefreshCw, run: registry.refresh, loading: registry.loading },
  ];
  const subtitle = registry.loading && registry.total === 0
    ? undefined
    : `${registry.total} ${pluralRu(registry.total, ["клиент", "клиента", "клиентов"])}${filtered ? " по отбору" : ""}`;

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title="Клиенты"
        subtitle={subtitle}
        menu={menu}
        menuLabel="Другие действия с клиентами"
        action={
          canManage ? (
            <Button variant="brand" onClick={() => setCreating(true)}>
              <Plus className="size-4" />
              Клиент
            </Button>
          ) : undefined
        }
      />

      <CustomersFilterBar query={query} onChange={setQuery} />

      <ResponsiveList
        aria-label="Реестр клиентов"
        rows={registry.items}
        columns={CUSTOMER_REGISTRY_COLUMNS}
        rowKey={(c) => c.id}
        rowHref={(c) => `${base}/${c.id}`}
        rowLabel={(c) => `Клиент ${customerTitle(c)}`}
        rowSize="double"
        loading={registry.loading}
        error={registry.error}
        onRetry={registry.refresh}
        filtered={filtered}
        skeletonRows={10}
        empty={{
          icon: Users,
          title: "Клиентов пока нет",
          description: canManage
            ? "Клиенты появятся после первого заказа на сайте — или добавьте клиента кнопкой «+ Клиент»"
            : "Клиенты появятся после первого заказа на сайте",
        }}
        filteredEmpty={{ icon: Search, title: "Нет клиентов по выбранным условиям", onReset: registry.resetFilters }}
      />

      <CrmPagination
        label="Страницы реестра клиентов"
        page={query.page}
        pageSize={query.pageSize}
        total={registry.total}
        onPageChange={(page) => setQuery({ page })}
        onPageSizeChange={(pageSize) => setQuery({ pageSize })}
      />

      {creating ? (
        <CustomerEditDialog
          open
          customer={null}
          onClose={() => setCreating(false)}
          onSaved={(c) => {
            setCreating(false);
            navigate(`${base}/${c.id}`);
          }}
        />
      ) : null}
    </div>
  );
}
